import { Injectable } from '@angular/core';
import { AuthService } from '../auth.service';
import { targetSuffix } from '../../_util/data-target';

const LAST_SEQ_KEY_PREFIX = 'notes:last_seq';
const LAST_SEQ_BY_SPACE_KEY_PREFIX = 'notes:last_seq_by_space';

@Injectable({ providedIn: 'root' })
export class SyncStateService {
  constructor(private auth: AuthService) {}

  // Each tenant's CouchDB change-feed sequence numbers are independent, so the
  // cursor must be scoped per account — otherwise switching accounts on the
  // same device would resume an incremental pull from a meaningless position.
  private get accountSuffix(): string {
    return `${targetSuffix()}::${this.auth.getUsername() ?? 'anonymous'}`;
  }

  /** Pre-multi-Space single cursor key. Read-only now (see getLastSeq()'s migration below) —
   * every user had exactly one database (their own), so this held that one cursor. Never written
   * to again once a per-Space entry exists for the Space it belonged to. */
  private get legacyKey(): string {
    return `${LAST_SEQ_KEY_PREFIX}${this.accountSuffix}`;
  }

  /** Every Space's database is a separate change feed with its own cursor (see
   * tasks/space_core_client.md, Stage 2 §4) — this key holds a `Record<spaceId, lastSeq>` for the
   * current account. */
  private get mapKey(): string {
    return `${LAST_SEQ_BY_SPACE_KEY_PREFIX}${this.accountSuffix}`;
  }

  private readMap(): Record<string, string> {
    try {
      const raw = localStorage.getItem(this.mapKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeMap(map: Record<string, string>): void {
    localStorage.setItem(this.mapKey, JSON.stringify(map));
  }

  /**
   * Reads the persisted change-feed cursor for one Space.
   *
   * `isPrivateSpace` should be true only for the account's own PRIVATE Space: the very first time
   * it's read after this per-Space change (i.e. it has no entry yet in the map), this falls back
   * to — and migrates into the map — the pre-multi-Space single cursor, so an existing user's sync
   * position for their own notes is not lost/reset just because Spaces now exist. Every other
   * Space (SHARED, or PRIVATE spaces not yet seen) has no such legacy value and correctly starts
   * from '0' (a full pull for a Space the user has never synced before).
   */
  getLastSeq(spaceId: string, isPrivateSpace = false): string {
    const map = this.readMap();
    if (map[spaceId] !== undefined) {
      return map[spaceId];
    }

    if (isPrivateSpace) {
      const legacy = localStorage.getItem(this.legacyKey);
      if (legacy !== null) {
        map[spaceId] = legacy;
        this.writeMap(map);
        return legacy;
      }
    }

    return '0';
  }

  setLastSeq(spaceId: string, seq: string): void {
    const map = this.readMap();
    map[spaceId] = seq;
    this.writeMap(map);
  }

  /** True once this account has synced at least one real Space (any cursor key besides `ignoreKey`,
   * the local-only legacy sentinel) — i.e. this backend is known to support Spaces. */
  hasSpaceCursors(ignoreKey: string): boolean {
    return Object.keys(this.readMap()).some((key) => key !== ignoreKey);
  }

  /** Resets one Space's cursor back to unset (next getLastSeq() for it returns '0'). */
  clearSpace(spaceId: string): void {
    const map = this.readMap();
    if (!(spaceId in map)) return;
    delete map[spaceId];
    this.writeMap(map);
  }

  /** Clears every Space's cursor for the current account, plus the legacy single-cursor key. */
  clear(): void {
    localStorage.removeItem(this.mapKey);
    localStorage.removeItem(this.legacyKey);
  }
}
